import { jsPDF } from 'jspdf';
import { CustomFieldByHierarchy } from '../../enquiryCustomFields/CustomFieldRepositores';
import { FieldType } from '../../enquiryCustomFields/selectors/CustomFieldTypeSelector';
import { EnquiryDetail, EnquiryDetailCustomField } from '../models/Enquiry';
import moment from 'moment';
import { formatNumber } from '../../helpers/ChartHelper';

const formatDate = (value: Date, format: string) => {
    return moment(value).format(format);
};

interface GenerateCustomFieldFileProps {
    fields: CustomFieldByHierarchy[] | undefined;
    values?: EnquiryDetailCustomField[];
    enquiryDetail?: EnquiryDetail;
    currency?: string | null;
    directDownload: boolean
}

export const GenerateCustomFieldFile = async (props: GenerateCustomFieldFileProps) => {

    const getGroupHeader = (group: CustomFieldByHierarchy) => {

        return `
            <div style="margin-bottom: 10px; font-size: 10pt; border-bottom: 1px solid #000; padding-bottom: 6px; font-weight: 600">
                <div style="color: #000;">${group.customFieldGroupName}</div>
                ${group.customFieldGroupDescription ? `
                    <div style="font-size: 8pt; color: #555; font-style: italic;">
                        ${group.customFieldGroupDescription}
                    </div>    
                ` : ''}
            </div>
        `;
    };

    const getGroupFields = (group: CustomFieldByHierarchy) => {

        return group.customField.filter((field) => field.isActive).map((field) => {

            const value = props.values?.find((v) => v.customFieldGuid === field.guid);

            if (field.customFieldType === FieldType.Label) {
                return `
                     <div style="margin-bottom: 10px; font-size: 8pt;">
                        <div>${field.description}</div>
                    </div>
                `;
            }
            if (field.customFieldType === FieldType['Yes/No']) {
                return `
                    <div style="margin-bottom: 10px; font-size: 8pt;">
                        <div>
                            ${field.description}${field.isMandatory ? ' *' : ''}
                        </div>
                        <div style="margin-top: 8px; display: flex; gap: 40px;">
                            <label
                                style="display: flex; align-items: center; gap: 5px;"
                            >
                                <input type="checkbox" disabled ${value?.boolValue === true ? 'checked' : ''} />
                                <span>Yes</span>    
                            </label>
                            <label
                                style="display: flex; align-items: center; gap: 5px;"
                            >
                                <input type="checkbox" disabled ${value?.boolValue === false ? 'checked' : ''}/> 
                                <span>No</span>
                            </label>
                        </div>
                        <small style="color: #555;">${field.name}</small>
                    </div>
                `;
            }

            if (field.customFieldType === FieldType['Multi Line']) { 
                return `
                    <div style="margin-bottom: 10px; font-size: 8pt; width: 100%">
                        <div style="display: block;">
                            ${field.name}${field.isMandatory ? ' *' : ''}
                        </div>
                        <div style="display: inline-block; border-bottom: 1px dotted #000; width: 100%;">
                            ${value?.multiLineTextValue || ''} &nbsp;
                        </div>
                        <small style="color: #555;">${field.description}</small>
                    </div>
                `;
            }

            if (field.customFieldType === FieldType.Date) {
                return `
                    <div style="margin-bottom: 10px; font-size: 8pt;">
                        <div style="display: block;">
                            ${field.name}${field.isMandatory ? ' *' : ''}
                        </div>
                        <div style="display: inline-block; border-bottom: 1px dotted #000; width: 100%;">
                            ${value?.dateValue ? formatDate(value.dateValue, 'DD/MM/YYYY') : ''} &nbsp;
                        </div>
                        <small style="color: #555;">${field.description}</small>
                    </div>
                `;
            }

            if (field.customFieldType === FieldType.Currency) {
                return `
                    <div style="margin-bottom: 10px; font-size: 8pt;">
                        <div style="display: block;">
                            ${field.name}${field.isMandatory ? ' *' : ''}
                        </div>
                        <div style="display: inline-block; border-bottom: 1px dotted #000; width: 100%;">
                            ${value?.currencyValue !== null && value?.currencyValue !== undefined ? `${props.currency}${formatNumber(value.currencyValue)}` : ''} &nbsp;
                        </div>
                        <small style="color: #555;">${field.description}</small>
                    </div>
                `;
            }

            return `
                <div style="margin-bottom: 10px; font-size: 8pt;">
                    <div style="display: block;">
                        ${field.name}${field.isMandatory ? '*' : ''}
                    </div>
                    <span style="display: block; border-bottom: 1px dotted #000; width: 100%;">
                        ${value?.textValue || value?.lookupListName || value?.numberValue || value?.integerValue || value?.emailValue || ' '} &nbsp;
                    </span>
                     <small style="color: #555;">${field.description}</small>
                </div>
            `;

            // <div style="margin-bottom: 10px; font-size: 8pt;">
            //     <strong>
            //         ${field.description}${field.isMandatory ? '*' : ''}
            //     </strong>
            //     <span style="display: inline-block; border-bottom: 1px solid #000; width: 150px; margin-left: 10px;">&nbsp;</span><br/>
            //     <small style="color: #555;">${field.name}</small>
            // </div>
        });
    };

    // <h2 style="text-align: center; text-transform: uppercase; margin-bottom: 30px; font-size: 10pt;">
    //     Information Form
    // </h2>

    const getDetails = () => {
        if (!props.enquiryDetail) {
            return '';
        }
        return `
            <div style="margin-bottom: 10px; padding-bottom: 6px;">
                <div style="color: #000;font-size: 10pt; font-weight: 600;">Enquiry Details</div>
                <div
                    style="display: flex; justify-content: space-between; font-size: 8pt; color: #555;"
                >
                    <div
                        style="display: flex; flex-direction: column; gap: 2px;"
                    >
                        <div>${props.enquiryDetail?.firstName} ${props.enquiryDetail?.lastName}</div>
                        <div>Email: ${props.enquiryDetail?.email}</div>
                        <div>Source: ${props.enquiryDetail?.source}</div>
                        <div>Area of Law: ${props.enquiryDetail?.areaOfLaw}</div>
                    </div>
                    <div
                        style="display: flex; flex-direction: column; gap: 2px; text-align: right;"
                    >
                        <div>Created ${props.enquiryDetail.createdDate ? formatDate(props.enquiryDetail.createdDate, 'DD MMM YYYY hh:mm a') : ''}</div>
                        
                        ${props.enquiryDetail.dateAssigned ? `
                            <div>Assigned ${formatDate(props.enquiryDetail.dateAssigned!, 'DD MMM YYYY hh:mm a')}</div>
                        ` : ''}
                    </div>
                </div>
            </div>
        `;
    };

    const htmlContent = `
        <div style="width: 423pt; font-family: Arial; sans-serif; font-size: 10pt; padding: 10pt 0pt";>
            ${getDetails()}
            ${props.fields?.map((group) => `
                ${getGroupHeader(group)}    
                ${getGroupFields(group).join('')}
            `).join('')}
        </div>
    `;

    // <div style="margin-top: 50px; font-size: 8pt;">
    //     <table style="width: 100%;">
    //         <tr>
    //         <td style="text-align: center;">
    //             ___________________________<br/>
    //             Signature
    //         </td>
    //         <td style="text-align: center;">
    //             ___________________________<br/>
    //             Date
    //         </td>
    //         </tr>
    //     </table>
    // </div>

    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    document.body.appendChild(container);

    const doc = new jsPDF({
        orientation: 'p',
        format: 'a4',
        unit: 'pt'
    });

    await doc.html(container, {
        margin: [15, 15, 15, 15],
        x: 0,
        y: 0,
        autoPaging: 'text'
        // html2canvas: { scale: 1 } // not too high
    });

    document.body.removeChild(container);

    const blob = doc.output('blob');
    const blobUrl = URL.createObjectURL(blob);

    if (props.directDownload) {
        return blob;
    }

    window.open(blobUrl, '_blank');
    return;
};